import { HttpClient } from '@angular/common/http';
import { Component, DestroyRef, NgZone, OnInit, afterNextRender, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  State,
  getExhibitionSettings,
  getExhibitionStyles,
  getLanguage,
  getLocalizedImages,
  getReloadRequired,
  getSelfRegistration,
  isLoggedInAsAdmin,
  notLoggedAndLoginMandatory
} from '@app/app.reducer';
import { AppService } from '@app/app.service';
import { ConteoLoginService } from '@app/login/conteo-login.service';
import { LoginService } from '@app/login/login.service';
import { FormName } from '@core/data-access/models/form/form.model';
import { BaseRoute } from '@core/data-access/models/routes.model';
import { selectIsHighContrastEnabled } from '@core/data-access/store/accessibility/accessibility.selectors';
import { injectStyleElement } from '@core/utils/dom-helpers';
import { MainIframeLayoutComponent } from '@layout/main-iframe-layout/main-iframe-layout.component';
import { MainLayoutComponent } from '@layout/main-layout/main-layout.component';
import { Store, select } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { TranslationsService } from '@pages/translations/translations.service';
import { getQueryParamsFromLocation, pageRunningInIframe } from '@shared/app-utils';
import { FormsService } from '@shared/forms/forms.service';
import { generateSkinCss } from '@shared/services-with-reducers/customization/customization.styles';
import { StatusBarService } from '@shared/status-bar/status-bar.service';
import { WINDOW } from '@shared/tokens';
import highContrast from '@src/assets/a11y/high-contrast.json';
import { environment } from '@src/environments/environment';
import {
  GetAllDepartments,
  GetAllOccupationalGroups,
  GetAllProfessions,
  GetAllTitles,
  GetListOfAllExhibitions
} from '@store/exhibition/exhibition.actions';
import { GetAllCountriesAction } from '@store/helpers/helper.actions';
import { HelperService } from '@store/helpers/helper.service';
import { StepName } from '@store/step-forms/step.interface';
import { getIsTranslationLoaded } from '@store/translation/translation.selectors';
import { UserService } from '@store/user/user.service';
import { isNil } from 'lodash';
import { catchError, combineLatest, filter, of, withLatestFrom } from 'rxjs';

@Component({
  selector: 'app-root',
  host: {
    '[class.admin]': 'isLoggedInAsAdmin()'
  },
  template: `
    @if (pageRunningInIframe) {
      <app-main-iframe-layout />
    } @else {
      <app-main-layout
        [isTranslationLoaded]="isTranslationLoaded()"
        [isSelfRegistration]="isSelfRegistration()"
        [isLoggedInAsAdmin]="isLoggedInAsAdmin()"
        [isReloadModalWindowOpen]="isReloadModalWindowOpen"
        [backgroundImage]="backgroundImage"
        (rightClickEvent)="onRightClick($event, isLoggedInAsAdmin())"
        (closeModalWindowEvent)="onCloseModalWindow($event)"
      />
    }
  `,
  standalone: true,
  imports: [TranslateModule, MainLayoutComponent, MainIframeLayoutComponent],
  providers: [ConteoLoginService]
})
export class AppComponent implements OnInit {
  readonly #destroyRef = inject(DestroyRef);
  readonly #window = inject(WINDOW);
  readonly #formsService = inject(FormsService);
  readonly #loginService = inject(LoginService);
  readonly #conteoLoginService = inject(ConteoLoginService);

  protected pageRunningInIframe = pageRunningInIframe();
  protected backgroundImage = '';
  protected isReloadModalWindowOpen = false;

  private favicon: HTMLLinkElement = document.querySelector('#favicon')!;
  private wasAppDisconnected = false;
  private resetByQueryParams: Array<string | { [key: string]: string }> = ['t', 'u'];

  protected readonly isTranslationLoaded = this.store.selectSignal(getIsTranslationLoaded);
  protected readonly isSelfRegistration = this.store.selectSignal(getSelfRegistration);
  protected readonly isLoggedInAsAdmin = this.store.selectSignal(isLoggedInAsAdmin);

  // we need to ensure all services globaly used initialize their singleton instance
  constructor(
    private router: Router,
    private ngZone: NgZone,
    private http: HttpClient,
    private translationsService: TranslationsService,
    private store: Store<State>,
    private appService: AppService,
    private statusBarService: StatusBarService,
    private helperService: HelperService,
    private userService: UserService
  ) {
    afterNextRender(() => this.appService.markAsRendered());

    this.initLoginServiceSubscriptions();
    this.initSetLoginFormValiditySubscription();

    this.resetAppWithLocationParams().then();

    // handle online / offline states
    this.appService.isOnline$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((connection) => {
      if (!connection) {
        this.statusBarService.setStatus('You are disconnected', 'error');
        this.wasAppDisconnected = true;
      }

      if (connection && this.wasAppDisconnected) {
        this.statusBarService.setStatus('You are online', 'success');
        this.wasAppDisconnected = false;
      }
    });

    this.store
      .pipe(
        select(getReloadRequired),
        filter((reloadRequired) => reloadRequired),
        takeUntilDestroyed()
      )
      .subscribe((reloadRequired) => {
        this.isReloadModalWindowOpen = reloadRequired;
      });

    this.ngZone.onError.pipe(takeUntilDestroyed()).subscribe((e) => {
      this.http
        .post(`${environment.protocol}${environment.webApiUrl}/audit-log/shop-error`, {
          error: e.toString(),
          stack: e.stack,
          userAgent: this.helperService.browserInfo()
        })
        .pipe(catchError((err) => of([])))
        .subscribe();
    });

    this.store.dispatch(new GetAllCountriesAction());
    this.store.dispatch(new GetAllTitles());
    this.store.dispatch(new GetAllProfessions());
    this.store.dispatch(new GetAllDepartments());
    this.store.dispatch(new GetAllOccupationalGroups());

    this.store
      .pipe(select(getLocalizedImages))
      .pipe(
        filter((images) => !!images),
        takeUntilDestroyed()
      )
      .subscribe((images) => {
        this.backgroundImage = images.background;
        this.favicon.href = images.favicon ? images.favicon : 'favicon.ico';
      });
  }

  ngOnInit() {
    this.processDataForAppInit();
  }

  protected onCloseModalWindow($event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();

    this.isReloadModalWindowOpen = false;

    this.appService.resetReducersWithUser().subscribe(() => {
      this.#window.location.reload();

      return;
    });
  }

  protected onRightClick($event: MouseEvent, isLoggedInAsAdmin: boolean) {
    if (!isLoggedInAsAdmin || this.router.url === `/${BaseRoute.Translations}`) {
      return;
    }

    this.translationsService.activeTranslation = '';

    const clickedElement = $event.target as HTMLElement;
    const dataTranslation = clickedElement.attributes['data-translation'];

    if (dataTranslation) {
      $event.preventDefault();

      this.translationsService.activeTranslation = dataTranslation.value;
    } else {
      const closestAncestorElement = clickedElement.closest('[data-nested-translation]');

      if (closestAncestorElement) {
        $event.preventDefault();

        this.translationsService.activeTranslation =
          closestAncestorElement.attributes['data-nested-translation']?.value;
      }
    }

    if (this.translationsService.activeTranslation) {
      this.router.navigate([`/${BaseRoute.Translations}`]);
    }
  }

  private processDataForAppInit() {
    // once styles in redux store are updated, ask customization service to add new style element with new stylesheet
    this.store
      .pipe(
        select(getExhibitionStyles),
        filter((exhibitionStyles) => !isNil(exhibitionStyles)),
        withLatestFrom(this.store.select(selectIsHighContrastEnabled)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(([exhibitionStyles, isHighContrastEnabled]) => {
        injectStyleElement(this.#window.document, exhibitionStyles.css, 'skin');

        if (isHighContrastEnabled) {
          injectStyleElement(this.#window.document, generateSkinCss(highContrast), 'accessibility-high-contrast-css');
        }
      });

    // once we switch language get new list of exhibitions
    this.store
      .select(getLanguage)
      .pipe(
        filter((language) => !!language),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((language) => {
        this.store.dispatch(new GetListOfAllExhibitions(language));
      });
  }

  /**
   * Reset App if location contains specific parameter.
   * We need to get the queryParams immediately (synchronously) therefore we are not taking it from ActivatedRoute,
   * as initial value of queryParams from ActivatedRoute is an empty Object
   */
  private resetAppWithLocationParams() {
    const locationQueryParams = getQueryParamsFromLocation();

    let resolutionPromise = new Promise((resolve) => {
      resolve(null);
    });
    let shouldResetAppState = false;

    for (const key in locationQueryParams) {
      const queryParam = locationQueryParams[key];
      const resetByQueryParam = this.resetByQueryParams.find((resetByQueryParam) => resetByQueryParam[key]);
      const resetByQueryParamValue = resetByQueryParam && resetByQueryParam[key];

      // reset on specific queryparam value
      if (resetByQueryParamValue && resetByQueryParamValue == queryParam) {
        shouldResetAppState = true;
      }
      // reset if queryParam is present
      else if (this.resetByQueryParams && this.resetByQueryParams.indexOf(key) !== -1) {
        shouldResetAppState = true;
      }
    }

    if (shouldResetAppState) {
      resolutionPromise = this.resetAppReducers();
    }

    return resolutionPromise;
  }

  private resetAppReducers() {
    const resetReducerPromise = new Promise<void>((resolve) => {
      this.appService.resetReducers().subscribe(() => resolve());
    });

    return resetReducerPromise;
  }

  private initLoginServiceSubscriptions() {
    this.userService.initLoginTimestampExpirationSubscription();
    this.userService.resetIsLogoutProcessingSubscription();
    this.#loginService.socialNetworkLoginQueryParamsSubscription();
    this.#conteoLoginService.initConteoLoginServiceSubscriptions();
  }

  private initSetLoginFormValiditySubscription() {
    return combineLatest({
      exhibitionSettings: this.store.select(getExhibitionSettings),
      notLoggedAndLoginMandatory: this.store.select(notLoggedAndLoginMandatory)
    })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(({ notLoggedAndLoginMandatory }) =>
        this.#formsService.setFormValidity(!notLoggedAndLoginMandatory, null, [StepName.Tickets, FormName.Login])
      );
  }
}
